import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Sparkles } from 'lucide-react';
import { heroContent } from '../../data/portfolio';

type HeroProps = {
  onScrollToProjects?: () => void;
};

const Hero: React.FC<HeroProps> = ({ onScrollToProjects }) => {
  return (
    <section
      id="home"
      className="relative overflow-hidden bg-gradient-to-b from-white via-white to-slate-50 pb-16 pt-28 dark:from-slate-950 dark:via-slate-950 dark:to-slate-900 sm:pt-32 lg:pt-36"
      aria-labelledby="hero-heading"
    >
      <div className="pointer-events-none absolute -left-40 top-0 h-72 w-72 rounded-full bg-hero-gradient opacity-40 blur-3xl dark:opacity-25" />
      <div className="pointer-events-none absolute -right-24 bottom-0 h-60 w-60 rounded-full bg-hero-gradient opacity-30 blur-3xl dark:opacity-20" />

      <div className="relative z-10 mx-auto flex max-w-6xl flex-col items-center gap-8 px-4 sm:gap-10 sm:px-6 lg:flex-row lg:items-end lg:gap-16 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: 'easeOut' }}
          className="flex-1"
        >
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-slate-200/80 bg-white/80 px-3.5 py-1.5 text-sm font-medium text-slate-600 shadow-sm backdrop-blur-md dark:border-slate-700/70 dark:bg-slate-900/80 dark:text-slate-300">
            <Sparkles className="h-3.5 w-3.5 text-brand-pink" />
            From Manual Work to Full Stack Developer
          </div>
          <h1
            id="hero-heading"
            className="text-balance text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl sm:leading-[1.05] md:text-5xl lg:text-[2.9rem] dark:text-slate-50"
          >
            {heroContent.name}
          </h1>
          <p className="mt-3 text-base font-medium text-slate-600 sm:text-lg dark:text-slate-300">
            {heroContent.title}
          </p>
          <p className="mt-4 max-w-xl text-sm text-slate-600 sm:text-base dark:text-slate-300">
            {heroContent.tagline}
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-2.5 sm:mt-8 sm:gap-3">
            <button
              type="button"
              onClick={onScrollToProjects}
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-hero-gradient px-6 py-2.5 text-sm font-semibold text-white shadow-glass transition hover:brightness-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-white focus-visible:ring-brand-pink/80 dark:focus-visible:ring-offset-slate-950"
            >
              {heroContent.ctaPrimary}
              <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
            </button>
            <a
              href="/media/cv/marco-biosa-cv.pdf"
              download="marco-biosa-cv.pdf"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-200/80 bg-white/70 px-5 py-2.5 text-sm font-medium text-slate-700 shadow-sm transition hover:border-brand-pink/70 hover:text-slate-900 dark:border-slate-700/80 dark:bg-slate-900/80 dark:text-slate-200 dark:hover:border-brand-pink/70"
            >
              {heroContent.ctaSecondary}
            </a>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.7, ease: 'easeOut', delay: 0.1 }}
          className="flex-1"
        >
          <div className="glass-card relative mx-auto flex h-56 w-full max-w-xs items-end overflow-hidden bg-glass-light p-3.5 sm:h-64 sm:max-w-sm sm:p-4 dark:bg-glass-dark">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_10%_20%,rgba(255,255,255,0.95)_0,transparent_60%),radial-gradient(circle_at_90%_10%,rgba(255,82,191,0.18)_0,transparent_55%),radial-gradient(circle_at_10%_90%,rgba(127,70,255,0.22)_0,transparent_60%)] opacity-90 dark:opacity-80" />
            <div className="relative z-10 flex w-full items-end justify-between">
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Focus
                </p>
                <p className="mt-1 text-sm font-semibold text-slate-900 dark:text-slate-50">
                  React · Flutter · Next.js
                </p>
                <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                  Building clean, accessible interfaces with real-world UX.
                </p>
              </div>
              <div className="flex flex-col items-end gap-3">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-tr from-brand-pink via-brand-purple to-brand-orange text-lg font-semibold text-white shadow-lg">
                  MB
                </div>
                <div className="rounded-full bg-slate-900/90 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-100 shadow-md dark:bg-white/95 dark:text-slate-900">
                  Portfolio 2025
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;



